class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleCheckout.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  handleCheckout(event) {
    if (!(event.target instanceof Element)) return;
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton || checkoutButton.disabled || checkoutButton.dataset.submitting === 'true') return;

    const formId = checkoutButton.getAttribute('form');
    const checkoutForm = formId ? document.getElementById(formId) : checkoutButton.form;
    if (!(checkoutForm instanceof HTMLFormElement)) return;

    event.preventDefault();
    if (window.Shopify?.designMode) {
      window.open('/checkout', '_blank', 'noopener');
      return;
    }

    checkoutButton.dataset.submitting = 'true';
    checkoutButton.setAttribute('aria-busy', 'true');
    checkoutButton.disabled = true;

    const checkoutField = document.createElement('input');
    checkoutField.type = 'hidden';
    checkoutField.name = 'checkout';
    checkoutField.value = 'checkout';
    checkoutForm.appendChild(checkoutField);
    HTMLFormElement.prototype.submit.call(checkoutForm);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const recommendationScrollPosition = {
      desktop: this.querySelector('.pr-cart-drawer__desktop-upsell-list')?.scrollTop || 0,
      mobile: this.querySelector('.pr-cart-drawer__recommendations-list')?.scrollLeft || 0,
    };

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const desktopRecommendations = this.querySelector('.pr-cart-drawer__desktop-upsell-list');
    const mobileRecommendations = this.querySelector('.pr-cart-drawer__recommendations-list');
    if (desktopRecommendations) desktopRecommendations.scrollTop = recommendationScrollPosition.desktop;
    if (mobileRecommendations) mobileRecommendations.scrollLeft = recommendationScrollPosition.mobile;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('pr-cart-drawer-upsell-nav')) {
  customElements.define(
    'pr-cart-drawer-upsell-nav',
    class PrCartDrawerUpsellNav extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.scroller = this.querySelector('.pr-cart-drawer__desktop-upsell-list');
        this.previousButton = this.querySelector('[data-upsell-scroll="previous"]');
        this.nextButton = this.querySelector('[data-upsell-scroll="next"]');

        if (!this.scroller || !this.previousButton || !this.nextButton) return;

        const { signal } = this.abortController;
        this.previousButton.addEventListener('click', () => this.scrollRecommendations(-1), { signal });
        this.nextButton.addEventListener('click', () => this.scrollRecommendations(1), { signal });
        this.scroller.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });

        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.scroller);
        requestAnimationFrame(() => this.updateControls());
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
      }

      scrollRecommendations(direction) {
        const distance = Math.max(this.scroller.clientHeight * 0.78, 240);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.scroller.scrollBy({ top: direction * distance, behavior });
      }

      updateControls() {
        const maxScrollTop = Math.max(0, this.scroller.scrollHeight - this.scroller.clientHeight);
        const isScrollable = maxScrollTop > 1;
        this.previousButton.hidden = !isScrollable;
        this.nextButton.hidden = !isScrollable;
        this.previousButton.disabled = !isScrollable || this.scroller.scrollTop <= 1;
        this.nextButton.disabled = !isScrollable || this.scroller.scrollTop >= maxScrollTop - 1;
      }
    },
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
